import { useAxios } from '@healthblocks-io/core'
import { useEffect, useRef } from 'react'
import useSWR from 'swr'

import type { MedicationFile } from '@/modules/app/types/HealthParameter.types'

const CACHE_KEY = 'medication-file-urls'

/** A SAS link carries its own access, so an `<img>` can load it without the bearer. */
const isSasUrl = (url: string) => url.includes('.blob.core.windows.net')

/**
 * The in-memory copy of a photo just uploaded, keyed by fileId, shown until
 * its stored url is in so the thumbnail does not blink.
 *
 * ponytail: module-level, so a preview whose photo is removed before its url
 * arrives lives until the page closes. Move it into context if that ever adds up.
 */
const localPreviews = new Map<string, string>()

/** Shows `url`, an object URL, for `fileId` until the stored url is in, then releases it. */
export function showLocalPreview(fileId: string, url: string) {
  localPreviews.set(fileId, url)
}

/**
 * Stored medication photos, keyed by fileId: their display urls, or when the
 * processed ones were processed.
 *
 * The api answers with a short-lived SAS link, so the urls are fetched when
 * the photos are shown rather than kept on the scheme. Urls already fetched are
 * reused while the scheme stays shown, so adding a photo fetches only that one;
 * showing it again fetches them all, so a file processed meanwhile shows that.
 * A photo whose url is not in yet, or that cannot be read, is simply absent
 * from the map.
 */
export function useStoredMedicationFiles(fileIds: string[]): ReadonlyMap<string, MedicationFile> {
  const axios = useAxios()
  // Not the SWR cache: that outlives the mount, and would keep the url of a
  // file processed since.
  const fetched = useRef(new Map<string, MedicationFile>())

  const getStoredFile = async (fileId: string): Promise<MedicationFile | undefined> => {
    const { data: file } = await axios.get<{
      key: string
      projectId: number
      url?: string
      meta?: { processedAt?: string }
    }>(`/file/${encodeURIComponent(fileId)}`, { params: { include: 'url' } })

    // Its contents are gone; the scheme shows that it was processed instead.
    if (file.meta?.processedAt) {
      return { processedAt: file.meta.processedAt }
    }

    if (!file.url) {
      return undefined
    }

    // A SAS link carries its own access, so the <img> can load it as is.
    if (isSasUrl(file.url)) {
      return { url: file.url }
    }

    // A driver without SAS links serves the photo behind the bearer, like
    // QuestionFileUpload's preview.
    // ponytail: this object URL is never revoked; fine for a handful of photos per session.
    const { data: blob } = await axios.get<Blob>(`/file/${file.key}/${file.projectId}/file`, { responseType: 'blob' })

    return { url: URL.createObjectURL(blob) }
  }

  // A plain object, not a Map: SWR compares results to tell whether they
  // changed, and does not look inside a Map.
  const getFileUrls = async () => {
    const files: Record<string, MedicationFile> = {}

    await Promise.all(
      fileIds.map(async (fileId) => {
        const stored = fetched.current.get(fileId) ?? (await getStoredFile(fileId).catch(() => undefined))
        if (stored) {
          fetched.current.set(fileId, stored)
          files[fileId] = stored
        }
      }),
    )

    return files
  }

  // Adding a photo changes the key; keeping the previous map stops the
  // thumbnails already shown from blinking while the new set loads.
  // Returning from the camera or picker counts as a focus, so revalidating on
  // it would fetch every photo again for nothing.
  // ponytail: a reused SAS link expires after 15 minutes; an image already
  // rendered keeps showing, a thumbnail first mounted after that would not.
  const { data } = useSWR(fileIds.length ? [CACHE_KEY, ...fileIds] : null, getFileUrls, {
    keepPreviousData: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  useEffect(() => {
    for (const fileId of Object.keys(data ?? {})) {
      const preview = localPreviews.get(fileId)
      if (preview) {
        URL.revokeObjectURL(preview)
        localPreviews.delete(fileId)
      }
    }
  }, [data])

  const files = new Map<string, MedicationFile>()
  for (const fileId of fileIds) {
    const preview = localPreviews.get(fileId)
    const stored = data?.[fileId] ?? (preview ? { url: preview } : undefined)
    if (stored) {
      files.set(fileId, stored)
    }
  }

  return files
}
